import type {CourseDefinitionContentResponse} from 'src/api/ProactApiClient';

export const languageNames: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  de: 'German',
  zh: 'Chinese',
  fr: 'French',
};

export function getLanguageDisplayName(
  code: string | null | undefined
): string {
  if (!code) return 'English';
  return languageNames[code] ?? code.toUpperCase();
}

export function getLanguageOptions(
  codes: string[]
): {value: string; label: string}[] {
  return codes.map(code => ({
    value: code,
    label: getLanguageDisplayName(code),
  }));
}

export function getAvailableLanguages(
  contents: CourseDefinitionContentResponse[],
  defaultLanguage: string = 'en'
): string[] {
  const langs = [...new Set(contents.map(c => c.language))];
  return langs.length > 0 ? langs.sort() : [defaultLanguage];
}

export function filterContentsByLanguage(
  contents: CourseDefinitionContentResponse[],
  language: string
): CourseDefinitionContentResponse[] {
  return contents.filter(c => c.language === language);
}

export function getContentLanguages(
  contents: CourseDefinitionContentResponse[]
): string[] {
  const langs = contents
    .map(c => c.language)
    .filter((code): code is string => !!code);
  return [...new Set(langs)].sort();
}

function joinWithOr(items: string[]): string {
  if (items.length <= 2) return items.join(' or ');
  return `${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}`;
}

// Mirrors CourseLanguageUtils.ValidateDefaultLanguageHasContent: any language is valid until content exists.
export function buildDefaultLanguageValidation(
  contentLanguages: string[]
): {excludeValues: string[]; customError: string} | undefined {
  if (contentLanguages.length === 0) return undefined;
  const withContent = contentLanguages.map(getLanguageDisplayName);
  return {
    excludeValues: Object.keys(languageNames).filter(
      code => !contentLanguages.includes(code)
    ),
    customError: `The selected language has no content. Choose ${joinWithOr(
      withContent
    )}, or add content in the selected language before making it the default language.`,
  };
}
